import React, { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

/**
 * "Live Fleet Map" — text-free hero. Dense UK pin grid with animated vans
 * travelling between depots. Glass login card floats on the right.
 */
const Login = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "";
  const dpdReturn = /^\/admin\/integrations\/dpd\/[\w-]+\/manual-login$/.test(from) ? from : null;
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role !== "driver" && dpdReturn ? dpdReturn : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) {
      toast.error("Enter email/driver ID and password");
      return;
    }
    setBusy(true);
    const res = await login(identifier.trim(), password);
    setBusy(false);
    if (!res.ok) return toast.error(res.error || "Login failed");
    toast.success(`Welcome back, ${res.user.full_name}`);
    nav(res.user.role === "driver" ? "/driver" : (dpdReturn || "/admin"), { replace: true });
  };

  // ~28 UK cities/towns on a 1000x1000 viewBox — roughly geo-accurate
  const pins = [
    { x: 620, y: 720, big: true },  // London
    { x: 640, y: 700 }, { x: 605, y: 740 }, { x: 660, y: 730 },   // London area
    { x: 500, y: 540, big: true },  // Birmingham
    { x: 490, y: 400, big: true },  // Manchester
    { x: 560, y: 380, big: true },  // Leeds
    { x: 520, y: 430 }, { x: 470, y: 460 },                       // Sheffield / Stoke
    { x: 400, y: 690, big: true },  // Cardiff
    { x: 370, y: 200, big: true },  // Glasgow
    { x: 220, y: 300, big: true },  // Belfast
    { x: 430, y: 260 },  // Edinburgh
    { x: 570, y: 300 },  // Newcastle
    { x: 470, y: 340 },  // Carlisle
    { x: 560, y: 460 },  // Nottingham
    { x: 580, y: 560 },  // Peterborough
    { x: 620, y: 610 },  // Cambridge
    { x: 700, y: 660 },  // Ipswich
    { x: 540, y: 780 },  // Southampton
    { x: 470, y: 780 },  // Exeter
    { x: 420, y: 760 },  // Plymouth
    { x: 720, y: 700 },  // Brighton
    { x: 690, y: 620 },  // Norwich
    { x: 460, y: 620 },  // Bristol
    { x: 380, y: 600 },  // Swansea
    { x: 240, y: 400 },  // Dublin (IE)
    { x: 200, y: 250 },  // Londonderry
    { x: 340, y: 150 },  // Inverness
  ];

  // Six routes for animated vans (SVG path d-attributes)
  const vanRoutes = [
    { d: "M 620 720 Q 570 640 500 540 Q 495 470 490 400 L 560 380",   dur: 22, delay: 0  },
    { d: "M 500 540 Q 450 620 400 690 Q 390 640 460 620 Q 480 580 500 540", dur: 26, delay: 3 },
    { d: "M 490 400 Q 430 300 370 200 Q 290 250 220 300",              dur: 30, delay: 6 },
    { d: "M 620 720 L 700 660 Q 690 620 620 610 Q 580 560 560 460",    dur: 24, delay: 1.5 },
    { d: "M 470 780 Q 420 760 380 600 Q 460 620 500 540",              dur: 28, delay: 9 },
    { d: "M 220 300 Q 290 250 370 200 Q 430 260 470 340 Q 480 380 490 400", dur: 32, delay: 12 },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-brand-navy text-white">
      {/* Base gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.20) 0%, transparent 55%), " +
            "radial-gradient(ellipse at 80% 80%, rgba(211,47,47,0.28) 0%, transparent 55%), " +
            "linear-gradient(160deg, #0b1424 0%, #0f172a 45%, #14213d 100%)",
        }}
      />
      {/* Grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"120\" height=\"120\"><filter id=\"n\"><feTurbulence baseFrequency=\"0.9\"/></filter><rect width=\"100%\" height=\"100%\" filter=\"url(%23n)\" opacity=\"0.5\"/></svg>')",
        }}
      />

      {/* UK map SVG with pins, routes and moving vans — zoomed tight so the map fills the screen */}
      <svg
        viewBox="120 100 720 780"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="pinGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.7" />
            <stop offset="60%" stopColor="#ef4444" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="vanGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#f87171" stopOpacity="1" />
            <stop offset="35%" stopColor="#ef4444" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#ef4444" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="routeStroke" x1="0%" x2="100%" y1="0%" y2="0%">
            <stop offset="0%" stopColor="#93c5fd" stopOpacity="0" />
            <stop offset="50%" stopColor="#93c5fd" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {/* UK silhouette (illustrative) */}
        <g fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="1.2">
          <path d="M 340 140 C 400 120, 460 150, 470 210 C 500 240, 520 290, 500 340
                    C 540 360, 560 420, 540 470 C 590 500, 610 560, 590 620
                    C 640 660, 660 720, 630 780 C 590 820, 520 830, 470 810
                    C 430 830, 380 820, 360 780 C 330 760, 320 720, 340 680
                    C 300 660, 290 610, 310 570 C 280 540, 280 480, 320 450
                    C 290 410, 300 350, 340 320 C 310 280, 310 210, 340 140 Z" />
          <path d="M 170 340 C 210 320, 260 340, 270 390 C 290 420, 280 470, 250 490
                    C 220 510, 180 500, 160 470 C 140 440, 150 380, 170 340 Z" />
        </g>

        {/* Route paths (visible) */}
        {vanRoutes.map((r, i) => (
          <path
            key={`route-${i}`}
            id={`route-${i}`}
            d={r.d}
            fill="none"
            stroke="url(#routeStroke)"
            strokeWidth="1.4"
            strokeDasharray="6 10"
            opacity="0.55"
            style={{ animation: `dashflow ${8 + i}s linear infinite` }}
          />
        ))}

        {/* Pulsing pins */}
        {pins.map((p, i) => (
          <g key={`pin-${i}`} transform={`translate(${p.x} ${p.y})`}>
            <circle r={p.big ? 32 : 18} fill="url(#pinGlow)">
              <animate attributeName="r"
                       values={p.big ? "18;36;18" : "10;22;10"}
                       dur={`${2.5 + (i % 5) * 0.4}s`}
                       begin={`${(i * 0.25) % 3}s`}
                       repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.9;0.15;0.9"
                       dur={`${2.5 + (i % 5) * 0.4}s`}
                       begin={`${(i * 0.25) % 3}s`}
                       repeatCount="indefinite" />
            </circle>
            <circle r={p.big ? 3.6 : 2.4} fill="#ef4444" />
            <circle r={p.big ? 1.6 : 1} fill="#fff" />
          </g>
        ))}

        {/* Moving vans — much bigger and glowing */}
        {vanRoutes.map((r, i) => (
          <g key={`van-${i}`}>
            {/* Outer bright halo */}
            <circle r="34" fill="url(#vanGlow)">
              <animateMotion dur={`${r.dur}s`} begin={`${r.delay}s`}
                              repeatCount="indefinite" rotate="auto">
                <mpath href={`#route-${i}`} />
              </animateMotion>
            </circle>
            {/* Van body (larger truck with more detail) */}
            <g>
              <animateMotion dur={`${r.dur}s`} begin={`${r.delay}s`}
                              repeatCount="indefinite" rotate="auto">
                <mpath href={`#route-${i}`} />
              </animateMotion>
              {/* Truck: cab + cargo box + wheels — RED & WHITE livery */}
              <g transform="translate(-24 -12)" filter="drop-shadow(0 2px 5px rgba(0,0,0,0.75))">
                {/* Cargo box (WHITE) */}
                <rect x="0" y="0" width="30" height="22" rx="2.5" fill="#ffffff" stroke="#0f172a" strokeWidth="1.4" />
                {/* Red horizontal stripe */}
                <rect x="1.5" y="9" width="27" height="4" fill="#ef4444" />
                {/* Small red brand mark on box */}
                <rect x="4" y="3" width="6" height="4" fill="#ef4444" opacity="0.85" />
                {/* Cab (RED) */}
                <rect x="30" y="4" width="12" height="18" rx="1.8" fill="#dc2626" stroke="#0f172a" strokeWidth="1.4" />
                {/* Windscreen */}
                <rect x="31.5" y="6.5" width="9" height="6.5" rx="0.6" fill="#dbeafe" opacity="0.95" />
                {/* Headlight */}
                <circle cx="42" cy="18.5" r="1.4" fill="#fff9c4" />
                {/* Wheels */}
                <circle cx="8" cy="23.5" r="3.6" fill="#0f172a" stroke="#475569" strokeWidth="0.8" />
                <circle cx="8" cy="23.5" r="1.4" fill="#64748b" />
                <circle cx="24" cy="23.5" r="3.6" fill="#0f172a" stroke="#475569" strokeWidth="0.8" />
                <circle cx="24" cy="23.5" r="1.4" fill="#64748b" />
                <circle cx="36" cy="23.5" r="3.6" fill="#0f172a" stroke="#475569" strokeWidth="0.8" />
                <circle cx="36" cy="23.5" r="1.4" fill="#64748b" />
              </g>
            </g>
          </g>
        ))}
      </svg>

      {/* Top bar: just the live indicator (logo moved to center) */}
      <div className="relative z-10 flex items-center justify-end px-6 md:px-12 pt-6 md:pt-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-white/70 font-mono">
          <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          Live
        </div>
      </div>

      {/* Centered floating logo — transparent PNG, no white box */}
      <div className="relative z-10 flex justify-center mt-2 md:mt-6 px-6 pointer-events-none">
        <img
          src="/mc-trading-logo.png"
          alt="MY CHOICE TRADING"
          className="h-32 md:h-48 w-auto object-contain"
          style={{
            filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 40px rgba(239,68,68,0.35))",
          }}
        />
      </div>

      {/* Centered glass login card */}
      <div className="relative z-10 min-h-[calc(100vh-80px)] flex items-center justify-center md:justify-end px-6 md:px-16">
        <div
          className="w-full max-w-sm rounded-2xl p-8 backdrop-blur-xl bg-white/[0.04] border border-white/10 shadow-2xl"
          style={{ boxShadow: "0 20px 60px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)" }}
        >
          <div className="md:hidden mb-4 flex justify-center">
            {/* mobile: card also gets the logo above via the centered layout */}
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-center md:text-left">Sign in</h2>
          <p className="text-sm text-white/50 mt-1 text-center md:text-left">Use your email or Driver ID.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-[10px] uppercase tracking-[0.2em] text-white/50">Email or Driver ID</Label>
              <Input
                id="identifier"
                data-testid="login-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@company.co.uk"
                className="mt-1.5 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-red focus-visible:border-brand-red"
                autoComplete="username"
                autoCapitalize="off"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] text-white/50">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-brand-red focus-visible:border-brand-red"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-11 bg-brand-red hover:bg-brand-red-hover text-white font-medium gap-2 group shadow-lg shadow-red-900/40"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Sign in <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" /></>
              )}
            </Button>
          </form>

          <div className="mt-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-white/40">
            <div className="flex-1 h-px bg-white/10" />
            or
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            data-testid="login-google"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href =
                "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
            }}
            className="mt-4 w-full h-11 rounded-md border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-white font-medium flex items-center justify-center gap-3 transition"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.4 2.3 30 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6.1C12.4 13.3 17.7 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.5 24.5c0-1.5-.2-3-.5-4.5H24v9h12.7c-.6 3-2.2 5.5-4.6 7.2l7.3 5.7c4.3-3.9 7.1-9.7 7.1-17.4z" />
              <path fill="#FBBC05" d="M10.4 28.6a14.4 14.4 0 0 1 0-9.2l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.6 2.6 10.7l7.8-6.1z" />
              <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.9l-7.3-5.7c-2 1.4-4.7 2.3-8.6 2.3-6.3 0-11.6-3.8-13.6-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
            </svg>
            Continue with Google
          </button>

          <p className="mt-6 text-[11px] text-center text-white/40 leading-relaxed">
            Trouble signing in? Contact your company admin.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes dashflow { to { stroke-dashoffset: -160; } }
      `}</style>
    </div>
  );
};

export default Login;
